import { SIZE, SPACING } from 'app-v2/design-tokens'
import { Layout } from '../../common/layout'
import React, { type ReactNode, type MutableRefObject, forwardRef } from 'react'
import { Spacer } from 'app-v2/design-systems/components/Spacer/Spacer'
import styled from 'styled-components'

import { H1, H4 } from 'app/common/typography'
import { colors } from 'app-v2/design-systems'
import { StyledBody } from '../duel/components/Body'
import { useWindowDimensions } from '../utils'
import { breakpoints } from 'app/assets/styles'

//$FlowFixMe
import surf from 'app/tu-preferes-king/assets/tpk-2024-surf.webp'
//$FlowFixMe
import buoy from 'app/tu-preferes-king/assets/tpk-2024-buoy.webp'
//$FlowFixMe
import header from 'app/tu-preferes-king/assets/tpk-2024-wave-header.webp'
//$FlowFixMe
import logo from 'app/tu-preferes-king/assets/tpk-2024-logo.webp'

type TPKLayoutProps = {
    children: ReactNode,
    header?: {
        title?: string,
        subTitle?: string,
        largeBackground?: boolean,
        mediumBackground?: boolean,
        withLogo?: boolean,
        withBuoy?: boolean,
        withSurf?: boolean
    },
    largeContainer?: boolean
}

export const TPKLayout = forwardRef(
    (
        {
            children,
            header: {
                title,
                subTitle,
                largeBackground = false,
                mediumBackground = false,
                withLogo = false,
                withSurf = largeBackground,
                withBuoy = largeBackground
            } = {},
            largeContainer = false
        }: TPKLayoutProps,
        ref: MutableRefObject<HTMLDivElement>
    ) => {
        const { width: windowWidth } = useWindowDimensions()

        const isSmall = windowWidth <= breakpoints.small

        return (
            <Layout color="transparent">
                <StyledPageContainer ref={ref}>
                    <StyledBackground
                        largeBackground={largeBackground}
                        mediumBackground={mediumBackground}
                        ref={ref}>
                        <StyledHeader>
                            {withSurf && (
                                <StyledSurfImage
                                    src={surf}
                                    width={isSmall ? 100 : 200}
                                />
                            )}
                            {withBuoy && (
                                <StyledBuoyImage
                                    src={buoy}
                                    width={isSmall ? 100 : 200}
                                />
                            )}

                            <StyledTitleContainer>
                                {withLogo && (
                                    <StyledLogo
                                        src={logo}
                                        width={isSmall ? 200 : 400}
                                    />
                                )}

                                {title && <H1 color={colors.white}>{title}</H1>}

                                {subTitle && (
                                    <>
                                        <Spacer size={SIZE.M} />

                                        <H4 color={colors.white}>{subTitle}</H4>
                                    </>
                                )}
                            </StyledTitleContainer>
                        </StyledHeader>
                    </StyledBackground>

                    <StyledBody>
                        <StyledCenteredContainer
                            largeBackground={largeBackground}>
                            <StyledContainer largeContainer={largeContainer}>
                                {children}
                            </StyledContainer>
                        </StyledCenteredContainer>
                    </StyledBody>

                    <Spacer size={SIZE.XXL} />
                </StyledPageContainer>
            </Layout>
        )
    }
)

const StyledLogo = styled.img`
    @media (max-width: ${breakpoints.small}px) {
        top: 100px;
        right: -50px;
    }
`

const StyledBuoyImage = styled.img`
    position: absolute;
    top: 0;
    right: 7%;

    @media (max-width: ${breakpoints.small}px) {
        top: 100px;
        right: -50px;
    }
`

const StyledSurfImage = styled.img`
    position: absolute;
    top: 50px;
    left: 5%;

    @media (max-width: ${breakpoints.small}px) {
        top: 10px;
        left: -50px;
    }
`

const StyledCenteredContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: ${({ largeBackground }) =>
        largeBackground ? '340px' : '300px'};
    position: relative;

    @media (max-width: ${breakpoints.small}px) {
        margin-top: 250px;
    }
`

const StyledContainer = styled.div`
    max-width: ${props => (props.largeContainer ? '800px' : '650px')};
    box-sizing: border-box;
    padding: ${SPACING.XXL} 0;

    @media (max-width: ${breakpoints.small}px) {
        width: 100%;
        padding: ${SPACING.L};
    }
`

const StyledBackground = styled.div`
    position: absolute;
    width: 100%;
    height: ${({ largeBackground, mediumBackground }) =>
        largeBackground ? '520px' : mediumBackground ? '460px' : '330px'};
    background-image: url(${header});
    background-position: bottom;
    background-repeat: no-repeat;
    background-size: cover;
    min-height: 300px;

    @media (max-width: ${breakpoints.small}px) {
        height: ${({ largeBackground, mediumBackground }) =>
            largeBackground ? '400px' : mediumBackground ? '500px' : '300px'};
    }
`

const StyledTitleContainer = styled.div`
    position: absolute;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    top: 120px;
    height: auto;
`

const StyledHeader = styled.div`
    box-sizing: border-box;
    margin-top: 150px;
    width: 100%;
    height: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${SPACING.S};
`

const StyledPageContainer = styled.div`
    margin: 0 auto;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: ${colors.mayoEggWhite};
    margin-top: ${props => (props.isForGoodies ? '-20px' : null)};
    gap: ${SPACING.XXL};
`
